import { useEffect, useRef, useState } from "react"
import cn from "classnames"
import { gsap } from "gsap"

import type { PeopleHeroHeadline } from "./PeopleHero.interface"
import s from "./PeopleHeroTicker.module.scss"

const INITIAL_HEADLINE_INDEX = 1

function getHeadlineMetrics(
  child: HTMLElement,
  viewportCenterY: number,
  falloffDistance: number,
) {
  const childRect = child.getBoundingClientRect()
  const childCenterY = childRect.top + childRect.height / 2
  const distanceFromCenter = Math.abs(childCenterY - viewportCenterY)

  if (falloffDistance === 0) return 1

  return Math.max(0, 1 - distanceFromCenter / falloffDistance)
}

function createHeadlineColorTween(el: HTMLParagraphElement) {
  return gsap.fromTo(
    el,
    {
      color: "var(--color-royal-blue)",
    },
    {
      color: "var(--color-text-inverse)",
      ease: "linear",
      paused: true,
    },
  )
}

export const PeopleHeroTicker = ({
  headlines,
}: {
  headlines: PeopleHeroHeadline[]
}) => {
  const animatableHeadlines = [...headlines, ...headlines.slice(0, 3)]

  const [activeIndex, setActiveIndex] = useState(INITIAL_HEADLINE_INDEX)

  const headingRefs = useRef<(HTMLParagraphElement | null)[]>([])
  const rootRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const track = trackRef.current
    const viewport = rootRef.current
    if (!track || !viewport) return

    let headingIndex = -1

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia()

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const getYOffset = (idx: number) => {
          const el = headingRefs.current[idx]
          const root = rootRef.current
          if (!el || !root) return 0

          // offsetTop/offsetHeight/scrollHeight are rounded to integers, so
          // snapping between two far-apart items (the loop reset) accumulates
          // sub-pixel rounding error and shows a jump. Measure with fractional
          // rects and derive the target from the current transform instead.
          const currentY = Number(gsap.getProperty(track, "y")) || 0
          const elRect = el.getBoundingClientRect()
          const rootRect = root.getBoundingClientRect()
          const elCenter = elRect.top + elRect.height / 2
          const rootCenter = rootRect.top + rootRect.height / 2

          return currentY + (rootCenter - elCenter)
        }

        const headlineColorTweens = headingRefs.current.map((el) =>
          el ? createHeadlineColorTween(el) : null,
        )

        const scrubHeadlines = () => {
          const viewportRect = viewport.getBoundingClientRect()
          const viewportCenterY = viewportRect.top + viewportRect.height / 2
          const falloffDistance = viewportRect.height / 2

          headingRefs.current.forEach((el, index) => {
            if (!el) return

            headlineColorTweens[index]?.progress(
              getHeadlineMetrics(el, viewportCenterY, falloffDistance * 0.75),
            )
          })
        }

        const tweenYOffset = (y: string | number) => {
          gsap.to(track, {
            y,
            delay: 1,
            ease: "power2.inOut",
            duration: 1.25,
            onUpdate: scrubHeadlines,
            onComplete: () => {
              scrubHeadlines()
              increment()
            },
          })
        }

        const increment = () => {
          headingIndex =
            headingIndex === animatableHeadlines.length - 3
              ? 0
              : headingIndex + 1

          if (headingIndex === 0) {
            gsap.set(track, { y: getYOffset(1) })
            headingIndex = 1
            scrubHeadlines()
          }

          tweenYOffset(getYOffset(headingIndex + 1))
          setActiveIndex(headingIndex)
        }

        gsap
          .timeline()
          .set(track, { y: getYOffset(1) })
          .to(track, {
            delay: 1,
            opacity: 1,
            duration: 0.5,
            ease: "power2.inOut",
            onUpdate: scrubHeadlines,
          })
          .then(() => {
            scrubHeadlines()
            increment()
          })

        scrubHeadlines()
      })
    }, viewport)

    return () => ctx.revert()
  }, [animatableHeadlines.length])

  return (
    <div
      ref={rootRef}
      className={s.headlineViewport}
      aria-live="off"
      aria-hidden="true"
    >
      <span aria-hidden="true" className={cn(s.headlineAnchor, s.headlineItem)}>
        Cultural Observers
      </span>
      <div className={s.headlineFade}>
        <div ref={trackRef} className={s.headlineTrack}>
          {animatableHeadlines.map((headline, index) => (
            <p
              key={index}
              ref={(el) => {
                headingRefs.current[index] = el
              }}
              className={cn(
                s.headlineItem,
                index === activeIndex && s["headlineItem--active"],
              )}
            >
              {headline!.text}
            </p>
          ))}
        </div>
      </div>
    </div>
  )
}
